import React, { useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
import { Select, SelectOption } from '@guildeducationinc/recess';
import uniqBy from 'lodash/uniqBy';
import LoadingPlaceholderField from '@src/components/Shared/FormInputFields/LoadingPlaceholderField/LoadingPlaceholderField';
import { Program, StudentFacingAcademicPartner } from '@src/graphql/profile_edit/queries/programs';

export const DEFAULT_OPTION = { label: 'Not found', value: 'not_found' } as SelectOption;

export const buildAcademicPartnerOptions = (
  academicPartners: StudentFacingAcademicPartner[]
): SelectOption[] => {
  return [
    ...academicPartners.map((academicPartner) => ({
      label: academicPartner.name,
      value: academicPartner.uuid,
    })),
    DEFAULT_OPTION,
  ];
};

export const getAcademicPartners = (programs: Program[]): StudentFacingAcademicPartner[] => {
  return uniqBy(
    programs
      .map((program: Program) => program.studentFacingAcademicPartner)
      .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })),
    'uuid'
  );
};

export interface AcademicPartnerSelectFieldProps {
  completionIndex: number;
  helpText: string;
  label: string;
  loading: boolean;
  name: string;
  programs: Program[];
}

const AcademicPartnerSelectField: React.FC<AcademicPartnerSelectFieldProps> = ({
  completionIndex,
  helpText,
  label,
  loading,
  name,
  programs,
}) => {
  const { handleChange, setFieldValue } = useFormikContext();
  const [field, meta] = useField({ name });

  const options = useMemo(() => buildAcademicPartnerOptions(getAcademicPartners(programs)), [
    programs,
  ]);

  if (loading) {
    return (
      <LoadingPlaceholderField
        helpText={helpText}
        inputId='academicPartnerSelectField'
        label={label}
        loading={loading}
        required={true}
      />
    );
  }

  return (
    <Select
      {...field}
      errors={meta.touched && meta.error ? meta.error : undefined}
      helpText={helpText}
      isClearable={false}
      isInvalid={!!meta.error && meta.touched}
      isSearchable={true}
      label={label}
      name={name}
      onBlur={field.onBlur}
      onChange={(e) => {
        handleChange(e);
        setFieldValue(`completions[${completionIndex}].programName`, '');
        setFieldValue(`completions[${completionIndex}].academicPartnerName`, '');
        setFieldValue(`completions[${completionIndex}].programUuid`, '');
        setFieldValue(`completions[${completionIndex}].programType`, '');
      }}
      options={options}
      required={true}
      value={field.value}
      setValue={(options, value) => {
        return options.filter((option) => option.value === value) || {};
      }}
    />
  );
};

export default AcademicPartnerSelectField;
